/**
 * LocationSearchInput – mirrors the mobile LocationSearchWidget (location_widget.dart).
 *
 * Behaviour parity with mobile:
 *  - Search input with 800 ms debounce (mobile uses Timer(Duration(milliseconds: 800)))
 *  - "Use Current Location" button via browser Geolocation + reverseGeocode()
 *  - Recent search history shown when input is empty (max 10 items from locationHistoryService)
 *  - Loading / empty-results / error states
 *  - Results filtered to places with city + state + country
 *  - Type-based icons (mirrors _buildPlaceIcon() in mobile)
 *  - On selection: saves to history, calls onLocationSelected(LocationBody), highlights selection
 *
 * Used in SearchLocationPage (signup step 6) and any other location-picker context.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiSearch,
  FiX,
  FiMapPin,
  FiNavigation,
  FiClock,
  FiHome,
  FiMap,
  FiAlertCircle,
  FiRefreshCw,
  FiTrash2,
} from 'react-icons/fi';
import { RiCheckboxCircleFill } from 'react-icons/ri';
import {
  MdOutlinePlace,
  MdOutlineLocalDining,
  MdOutlineHotel,
  MdOutlineSchool,
  MdOutlineAtm,
  MdOutlineLocalGasStation,
  MdOutlinePark,
  MdOutlineStore,
} from 'react-icons/md';
import {
  searchPlaces,
  reverseGeocode,
  placeToLocationBody,
  getPrimaryName,
  getSecondaryName,
  type Place,
  type LocationBody,
} from '../../services/locationService';
import { locationHistoryService } from '../../services/locationHistoryService';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LocationSearchInputProps {
  /** Called with the selected LocationBody (or null when the selection is cleared). */
  onLocationSelected: (location: LocationBody | null) => void;
  /** Initial selected location (e.g. when returning to this step). */
  initialLocation?: LocationBody | null;
  placeholder?: string;
  /** If true, relaxes the city+state+country validation for results (lax mode). */
  lax?: boolean;
}

type ViewState = 'idle' | 'loading' | 'results' | 'empty' | 'error' | 'gps-loading';

// ─── Icon mapping ─────────────────────────────────────────────────────────────
// Mirrors _buildPlaceIcon() in location_widget.dart

function PlaceIcon({ type, className }: { type: string; className?: string }) {
  const t = type.toLowerCase();
  const base = className ?? 'text-[#3B82F6]';

  if (t.includes('street_address') || t.includes('premise') || t.includes('subpremise')) {
    return <FiHome className={base} size={18} />;
  }
  if (t.includes('route') || t.includes('intersection')) {
    return <FiNavigation className={base} size={18} />;
  }
  if (
    t.includes('political') ||
    t.includes('locality') ||
    t.includes('sublocality') ||
    t.includes('geocode')
  ) {
    return <MdOutlinePlace className={base} size={18} />;
  }
  if (t.includes('restaurant') || t.includes('food') || t.includes('bakery')) {
    return <MdOutlineLocalDining className={base} size={18} />;
  }
  if (t.includes('lodging') || t.includes('hotel')) {
    return <MdOutlineHotel className={base} size={18} />;
  }
  if (t.includes('school') || t.includes('university') || t.includes('library')) {
    return <MdOutlineSchool className={base} size={18} />;
  }
  if (t.includes('bank') || t.includes('atm') || t.includes('finance')) {
    return <MdOutlineAtm className={base} size={18} />;
  }
  if (t.includes('gas_station') || t.includes('fuel')) {
    return <MdOutlineLocalGasStation className={base} size={18} />;
  }
  if (t.includes('park') || t.includes('natural_feature')) {
    return <MdOutlinePark className={base} size={18} />;
  }
  if (t.includes('store') || t.includes('shopping')) {
    return <MdOutlineStore className={base} size={18} />;
  }
  if (t.includes('establishment') || t.includes('point_of_interest')) {
    return <FiMap className={base} size={18} />;
  }
  return <FiMapPin className={base} size={18} />;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const LocationSearchInput: React.FC<LocationSearchInputProps> = ({
  onLocationSelected,
  initialLocation = null,
  placeholder,
  lax = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Place[]>([]);
  const [history, setHistory] = useState<Place[]>([]);
  const [viewState, setViewState] = useState<ViewState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationBody | null>(initialLocation);

  // User location context – used for display purposes only (GPS not used for search bias on web)
  const [userCity, setUserCity] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const lastErrorSourceRef = useRef<'search' | 'gps' | null>(null);
  const gpsRetryCount = useRef(0);

  // ── On mount: load history + try to get user city for placeholder ────────────
  async function _getUserCity() {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const place = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          if (place?.city) setUserCity(place.city);
        } catch {
          // silent – just for placeholder text
        }
      },
      () => {
        // permission denied – ignore
      },
      { timeout: 5000 },
    );
  }

  useEffect(() => {
    setHistory(locationHistoryService.getHistory());
    _getUserCity();
  }, []);

  // ── Search input change → 800 ms debounce ────────────────────────────────────
  const _performSearch = useCallback(
    async (q: string) => {
      try {
        const places = await searchPlaces(q, 8, !lax);
        setResults(places);
        setViewState(places.length > 0 ? 'results' : 'empty');
        setErrorMessage('');
      } catch (err: unknown) {
        const msg = (err as Error)?.message ?? 'Search failed';
        setErrorMessage(msg);
        lastErrorSourceRef.current = 'search';
        setViewState('error');
      }
    },
    [lax],
  );

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);

      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.trim().length < 2) {
        setResults([]);
        setErrorMessage('');
        setViewState('idle');
        return;
      }

      setViewState('loading');
      debounceRef.current = setTimeout(() => {
        _performSearch(value.trim());
      }, 800); // mirrors mobile Timer(Duration(milliseconds: 800))
    },
    [_performSearch],
  );

  // ── "Use Current Location" ───────────────────────────────────────────────────
  async function handleUseCurrentLocation() {
    if (!navigator.geolocation) {
      setErrorMessage('Geolocation is not supported by your browser.');
      setViewState('error');
      return;
    }

    setViewState('gps-loading');
    setErrorMessage('');

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        gpsRetryCount.current = 0;
        try {
          const place = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          if (place) {
            _selectPlace(place);
          } else {
            // Fallback: build a minimal LocationBody from raw coords
            const fallback: LocationBody = {
              address: 'Current Location',
              city: '',
              state: '',
              country: '',
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
            };
            setSelectedLocation(fallback);
            onLocationSelected(fallback);
          }
          setViewState('idle');
        } catch {
          setErrorMessage('Could not determine your location. Try searching manually.');
          lastErrorSourceRef.current = 'gps';
          setViewState('error');
        }
      },
      (err) => {
        const msgs: Record<number, string> = {
          1: 'Location permission denied. Please allow access in your browser settings.',
          2: 'Location temporarily unavailable. This is usually transient — tap Retry to try again.',
          3: 'Location request timed out. Please try again.',
        };
        const isTransient = err.code === 2;
        setErrorMessage(msgs[err.code] ?? 'Could not access your location.');
        lastErrorSourceRef.current = 'gps';
        setViewState('error');
        // Auto-retry once after a short delay for transient kCLErrorLocationUnknown failures
        if (isTransient && gpsRetryCount.current === 0) {
          gpsRetryCount.current += 1;
          setTimeout(() => handleUseCurrentLocation(), 1500);
        }
      },
      { timeout: 12000, enableHighAccuracy: false },
    );
  }

  // ── Select a place ───────────────────────────────────────────────────────────
  function _selectPlace(place: Place) {
    if (!lax) {
      if (!place.city?.trim() || !place.state?.trim() || !place.country?.trim()) {
        setErrorMessage('Incomplete location – please select a more specific result.');
        setViewState('error');
        return;
      }
    }

    locationHistoryService.saveToHistory(place);
    setHistory(locationHistoryService.getHistory());

    const locationBody = placeToLocationBody(place);
    setSelectedLocation(locationBody);
    onLocationSelected(locationBody);

    // Show the selection summary
    setQuery('');
    setResults([]);
    setViewState('idle');
  }

  // ── Clear selection ──────────────────────────────────────────────────────────
  function handleClearSelection() {
    setSelectedLocation(null);
    onLocationSelected(null);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  // ── Clear history ────────────────────────────────────────────────────────────
  function handleClearHistory() {
    locationHistoryService.clearHistory();
    setHistory([]);
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  const effectivePlaceholder =
    placeholder ?? (userCity ? `Search places in ${userCity}…` : 'Search for a city or address…');

  return (
    <div className="w-full flex flex-col gap-3">
      {/* ── Selected location summary ─────────────────────────────────── */}
      {selectedLocation && (
        <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <div className="w-9 h-9 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center shrink-0">
            <RiCheckboxCircleFill className="text-green-400" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-[14px] font-semibold truncate">
              {selectedLocation.address !== 'Current Location' &&
              !selectedLocation.address.includes(selectedLocation.city)
                ? `${selectedLocation.address}, ${selectedLocation.city}`
                : [selectedLocation.city, selectedLocation.state, selectedLocation.country]
                    .filter(Boolean)
                    .join(', ') || selectedLocation.address}
            </p>
            {selectedLocation.latitude !== undefined &&
              selectedLocation.longitude !== undefined && (
                <p className="text-zinc-500 text-[11px] mt-0.5">
                  {selectedLocation.latitude?.toFixed(4)}, {selectedLocation.longitude?.toFixed(4)}
                </p>
              )}
          </div>
          <button
            type="button"
            onClick={handleClearSelection}
            className="text-zinc-500 hover:text-white transition-colors cursor-pointer shrink-0 text-[12px] font-medium"
          >
            Change
          </button>
        </div>
      )}

      {/* ── Search input ─────────────────────────────────────────────── */}
      {!selectedLocation && (
        <>
          <div className="relative flex items-center bg-[#0a0a0a] border border-white/12 rounded-xl h-12 px-4 focus-within:border-blue-500/50 transition-colors touch-action-manipulation">
            <FiSearch className="text-zinc-500 mr-3 shrink-0" size={18} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
              onInput={(e) => handleQueryChange((e.target as HTMLInputElement).value)}
              placeholder={effectivePlaceholder}
              autoComplete="off"
              className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 w-full focus:outline-none focus:ring-0 touch-target"
              style={{ touchAction: 'manipulation' }}
            />
            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setResults([]);
                  setViewState('idle');
                }}
                className="text-zinc-500 hover:text-zinc-300 transition-colors cursor-pointer ml-2 shrink-0"
              >
                <FiX size={16} />
              </button>
            )}
          </div>

          {/* ── Use Current Location button ─────────────────────────── */}
          <button
            type="button"
            onClick={handleUseCurrentLocation}
            disabled={viewState === 'gps-loading'}
            className="flex items-center justify-center gap-2 h-10 w-full bg-[#3B82F6]/10 hover:bg-[#3B82F6]/20 border border-[#3B82F6]/30 rounded-xl text-[#3B82F6] text-[14px] font-medium transition-colors cursor-pointer disabled:opacity-50"
          >
            {viewState === 'gps-loading' ? (
              <>
                <div className="w-4 h-4 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
                Detecting location…
              </>
            ) : (
              <>
                <FiNavigation size={16} />
                Use Current Location
              </>
            )}
          </button>

          {/* ── Content area ────────────────────────────────────────── */}
          <div className="w-full">
            {/* Loading */}
            {viewState === 'loading' && (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
                <p className="text-zinc-500 text-[14px]">Searching locations…</p>
              </div>
            )}

            {/* Error */}
            {viewState === 'error' && (
              <div className="flex flex-col items-center justify-center py-8 gap-4 px-4">
                <div className="w-14 h-14 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center">
                  <FiAlertCircle className="text-red-400" size={28} />
                </div>
                <div className="text-center">
                  <p className="text-white text-[15px] font-semibold mb-1">Search Error</p>
                  <p className="text-zinc-500 text-[13px] leading-relaxed max-w-xs">
                    {errorMessage}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (lastErrorSourceRef.current === 'gps') {
                      handleUseCurrentLocation();
                    } else if (query.length >= 2) {
                      _performSearch(query);
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[14px] font-medium rounded-lg transition-colors cursor-pointer"
                >
                  <FiRefreshCw size={14} />
                  Retry
                </button>
              </div>
            )}

            {/* Empty results */}
            {viewState === 'empty' && (
              <div className="flex flex-col items-center justify-center py-10 gap-3 px-4 text-center">
                <div className="w-14 h-14 rounded-2xl bg-zinc-800 border border-white/12 flex items-center justify-center">
                  <FiSearch className="text-zinc-500" size={26} />
                </div>
                <p className="text-white text-[15px] font-semibold">No places found</p>
                <p className="text-zinc-500 text-[13px]">
                  Try different keywords or check spelling
                </p>
              </div>
            )}

            {/* Results list */}
            {viewState === 'results' && results.length > 0 && (
              <div className="flex flex-col gap-1 max-h-72 overflow-y-auto pr-1 custom-scrollbar">
                {results.map((place) => (
                  <button
                    key={place.placeId}
                    type="button"
                    onClick={() => _selectPlace(place)}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      _selectPlace(place);
                    }}
                    className="flex items-center gap-3 w-full p-3 bg-[#0a0a0a] hover:bg-[#111] border border-white/12 rounded-xl transition-colors text-left cursor-pointer group active:bg-[#1a1a1a] touch-action-manipulation"
                    style={{ touchAction: 'manipulation' }}
                  >
                    {/* Type icon */}
                    <div className="w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0 group-hover:bg-blue-500/20 transition-colors">
                      <PlaceIcon type={place.type} />
                    </div>

                    {/* Text */}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-[14px] font-semibold truncate">
                        {getPrimaryName(place)}
                      </p>
                      {getSecondaryName(place) && (
                        <p className="text-zinc-500 text-[12px] truncate mt-0.5">
                          {getSecondaryName(place)}
                        </p>
                      )}
                      <p className="text-zinc-700 text-[11px] mt-0.5 tabular-nums">
                        {place.lat.toFixed(4)}, {place.lon.toFixed(4)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Idle: welcome + history */}
            {viewState === 'idle' && !query && (
              <div className="flex flex-col gap-4">
                {/* Welcome card (only when history is empty) */}
                {history.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
                    <div className="w-16 h-16 rounded-3xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                      <FiMapPin className="text-[#3B82F6]" size={32} />
                    </div>
                    <p className="text-white text-[18px] font-semibold">Find Your Location</p>
                    <p className="text-zinc-500 text-[13px] leading-relaxed max-w-xs">
                      Search for addresses, places, or use your current location
                    </p>
                  </div>
                )}

                {/* Recent searches */}
                {history.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-zinc-400 text-[12px] font-semibold uppercase tracking-wider flex items-center gap-1.5">
                        <FiClock size={12} />
                        Recent Searches
                      </p>
                      <button
                        type="button"
                        onClick={handleClearHistory}
                        className="text-zinc-600 hover:text-red-400 text-[12px] flex items-center gap-1 transition-colors cursor-pointer"
                      >
                        <FiTrash2 size={12} />
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-col gap-1">
                      {history.map((place) => (
                        <button
                          key={place.placeId || place.displayName}
                          type="button"
                          onClick={() => _selectPlace(place)}
                          onTouchEnd={(e) => {
                            e.preventDefault();
                            _selectPlace(place);
                          }}
                          className="flex items-center gap-3 w-full p-3 bg-[#0a0a0a] hover:bg-[#111] border border-white/12 rounded-xl transition-colors text-left cursor-pointer group active:bg-[#1a1a1a] touch-action-manipulation"
                          style={{ touchAction: 'manipulation' }}
                        >
                          <div className="w-9 h-9 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center shrink-0">
                            <FiClock className="text-zinc-500" size={16} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-[14px] font-semibold truncate">
                              {getPrimaryName(place)}
                            </p>
                            {getSecondaryName(place) && (
                              <p className="text-zinc-500 text-[12px] truncate mt-0.5">
                                {getSecondaryName(place)}
                              </p>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
